import type { FilterSchema } from '@/shared/components/filter/validation'
import type { SRT_SortingState } from '@/shared/components/master-table'
import type { TFunction } from 'i18next'

import { useMemo, useState } from 'react'
import {
	isCompetitorFilter,
	isProductFilter,
} from '@/pages/dashboard/lib/filters'
import { keyBy } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useCPIQuery } from '@/entities/bi'
import { useCompetitorsOptionsQuery } from '@/entities/core'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Progress, ProgressBar } from '@/shared/components/Progress'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import Widget from '../../Widget/Widget'
import WidgetContent from '../../Widget/WidgetContent'
import WidgetHeader from '../../Widget/WidgetHeader'

export default function CompetitorCPIWidget({
	commonFilters,
	className,
}: {
	commonFilters: FilterSchema[]
	className?: string
}) {
	const { t } = useTranslation()

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.competitor_cpi.title')}
				tooltip={t('widget.competitor_cpi.tooltip')}
				subtitle={t('widget.competitor_cpi.description')}
			/>
			<WidgetContent className='truncate px-0'>
				<ErrorBoundary fallback={<ErrorFallback />}>
					<CompetitorCPITable commonFilters={commonFilters} />
				</ErrorBoundary>
			</WidgetContent>
		</Widget>
	)
}

const CompetitorCPITable = ({
	commonFilters,
}: {
	commonFilters: FilterSchema[]
}) => {
	const { t } = useTranslation()
	const cpiQuery = useCPIQuery({
		product_filters: commonFilters.filter(isProductFilter) || [],
		cd_filters: commonFilters.filter(isCompetitorFilter) || [],
		dimensions: ['competitor_name' as const],
		label: 'SubcategoryPerformance',
	})
	const competitorsQuery = useCompetitorsOptionsQuery({ limit: 0 })
	const rows = useMemo(() => {
		if (!competitorsQuery.data || !cpiQuery.data) return []

		const cpis = cpiQuery.data.map((item) => item.cpi) || []
		const maxCpi = Math.max(...cpis)
		const minCpi = Math.min(...cpis)

		const cpiMap = keyBy('competitor_name', cpiQuery.data)

		return (
			competitorsQuery.data?.objects
				?.filter((competitor) => competitor.name! && competitor.name in cpiMap)
				?.map((competitor) => ({
					competitor_name: competitor.name!,
					percentage: calculatePercentage(
						minCpi,
						maxCpi,
						cpiMap[competitor.name!]?.cpi
					),
					cpi: cpiMap[competitor.name!]?.cpi,
					products_count: cpiMap[competitor.name!]?.products_count,
				})) || []
		)
	}, [competitorsQuery.data, cpiQuery.data])

	const columns = useMemo(() => getColumns(t), [t])
	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			id: 'cpi',
			desc: false,
		},
	])
	const isLoading = cpiQuery.isLoading || competitorsQuery.isLoading
	const isError = cpiQuery.isError || competitorsQuery.isError

	const table = useMasterReactTable({
		columns,
		data: rows,
		layoutMode: 'grid',
		sortUndefined: 'first',
		enableTableHead: false,
		divideX: false,
		state: {
			sorting,
			isLoading,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		enableColumnActions: false,
		enableColumnResizing: false,
		onSortingChange: setSorting,
		getRowHeight(row) {
			return 48
		},
	})
	if (isError) {
		return <ErrorFallback />
	}

	return (
		<div className='fraction-table border-base relative flex max-h-55 flex-1 flex-col overflow-hidden border-y border-solid'>
			<MasterTable table={table} />
		</div>
	)
}
const ErrorFallback = () => {
	const { t } = useTranslation()

	return (
		<Empty className='md:px-4'>
			<EmptyHeader>
				<EmptyMedia
					variant='icon'
					className='bg-destructive/20 text-destructive'
				>
					<AlertTriangleIcon />
				</EmptyMedia>
				<EmptyDescription className='text-destructive'>
					{t('error.unrecoverable.title')}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}

const calculatePercentage = (min: number, max: number, cpi: number) => {
	const gap = 10

	return gap + ((cpi - min) / (max - min)) * (100 - gap)
}

type SubcategoryPerfomanceRow = {
	competitor_name: string
	percentage: number
	cpi?: number
	products_count: number
}
const columnHelper = createSRTColumnHelper<SubcategoryPerfomanceRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('competitor_name', {
		id: 'competitor_name',
		header: t('fapp.competitor', {
			ns: 'fields',
			defaultValue: 'Category Name',
		}),

		enableSorting: true,
		Cell: ({ cellValue, row }) => (
			<div className='flex flex-col items-start gap-1 py-1'>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className='truncate text-xs'>
							{cellValue === null || cellValue === undefined ? '-' : cellValue}
						</div>
					</TooltipTrigger>
					<TooltipContent>{cellValue}</TooltipContent>
				</Tooltip>
				<div className='flex items-center gap-1 text-left text-xs'>
					<span className='text-muted-foreground'>SKUs: </span>
					<span className='font-medium text-foreground'>
						{formatNumber(row.original.products_count, NumberFormats.Integer)}
					</span>
				</div>
			</div>
		),
	}),
	columnHelper.accessor('percentage', {
		id: 'percentage',
		header: t('col_percentage', {
			defaultValue: 'Percentage',
		}),
		Cell: ({ cellValue }) => (
			<Progress>
				<ProgressBar className={getColor(cellValue)} value={cellValue} />
			</Progress>
		),
		size: 250,
	}),
	columnHelper.accessor('cpi', {
		id: 'cpi',
		header: t('col_cpi_short', {
			defaultValue: 'CPI',
		}),
		Cell: ({ cellValue, row }) => (
			<div className='flex flex-col justify-center gap-1 py-2'>
				<div className='cell-data-number text-xs'>
					{cellValue === null || cellValue === undefined
						? '-'
						: formatNumber(cellValue, NumberFormats.Fixed)}
				</div>
			</div>
		),
		size: 80,
	}),
]

const getColor = (percentage: number) => {
	if (percentage >= 0 && percentage <= 25) {
		return 'bg-blue-500'
	}
	if (percentage > 25 && percentage <= 50) {
		return 'bg-green-500'
	}
	if (percentage > 50 && percentage <= 75) {
		return 'bg-yellow-500'
	}

	return 'bg-red-500'
}
